import type { ApolloClient, RefetchQueriesInclude } from '@apollo/client';

/**
 * Lists backed by the search index lag the write behind them: a refetch fired the
 * instant a create/delete mutation resolves usually comes back without the change, and
 * nothing refetches again, so the list stays wrong until a page reload. These helpers
 * refetch on a short backoff until the result agrees with the write.
 */

/** Node ids present in the results of a `client.refetchQueries` round. */
export function nodeIdsIn(results: readonly unknown[]): Set<string> {
  const ids = new Set<string>();
  for (const result of results) {
    const edges = (result as { data?: { notes?: { edges?: ({ node?: { id?: string | null } } | null)[] } } })
      ?.data?.notes?.edges;
    for (const edge of edges ?? []) {
      if (edge?.node?.id) ids.add(edge.node.id);
    }
  }
  return ids;
}

interface RefetchUntilSettledOptions {
  /** What the first, immediate round refetches. */
  firstInclude: RefetchQueriesInclude;
  /** What each retry refetches — usually narrower than the first round. */
  retryInclude: RefetchQueriesInclude;
  /** Delay before each retry, in ms. One retry per entry, then give up. */
  delaysMs: readonly number[];
  /** Receives the node ids a round came back with; true once the write is visible. */
  isSettled: (ids: Set<string>) => boolean;
}

/**
 * Refetch until `isSettled` agrees with the results, retrying on `delaysMs`.
 * Resolves true once settled, false if the index never caught up (the last result is
 * left in place), nothing matching is mounted any more, or a refetch failed. Never
 * throws: callers have already confirmed the write to the user, so a slow index must
 * not turn into an error.
 */
export async function refetchUntilSettled(
  client: Pick<ApolloClient<unknown>, 'refetchQueries'>,
  { firstInclude, retryInclude, delaysMs, isSettled }: RefetchUntilSettledOptions,
): Promise<boolean> {
  try {
    let results = await client.refetchQueries({ include: firstInclude });
    if (isSettled(nodeIdsIn(results))) return true;

    for (const delay of delaysMs) {
      await new Promise(resolve => setTimeout(resolve, delay));
      results = await client.refetchQueries({ include: retryInclude });
      // The list was unmounted (the user navigated away): nothing left to correct,
      // and it will fetch fresh when it mounts again.
      if (results.length === 0) return false;
      if (isSettled(nodeIdsIn(results))) return true;
    }
  } catch (error) {
    console.error('Error refreshing index-backed lists:', error);
  }
  return false;
}
